/**
 * Checkout — single-store conversion-optimized checkout.
 *
 * Sections (top → bottom):
 *   1. Delivery address (LocationPicker + flat/floor/landmark line)
 *   2. Special delivery instructions
 *   3. CouponInput (input + available offers list, auto-suggest)
 *   4. TipSelector for the delivery agent
 *   5. PaymentMethodSelector (UPI / COD / card / wallet)
 *   6. TrustBadges + Place order CTA
 *
 *   Right-rail OrderSummaryCard sticks while the user scrolls the form.
 *
 * The tip is summed into the displayed `total` so users see exactly what
 * they pay. (Server doesn't yet accept a tip field — the v1 backend rolls
 * it into the order on a follow-up patch; the UI is forward-compatible.)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { ArrowLeft, MapPin, Truck } from 'lucide-react';
import { cartAPI, Cart } from '../api/cart';
import { ordersAPI, CreateOrderRequest } from '../api/orders';
import { productsAPI, Product } from '../api/products';
import { storesAPI, Store } from '../api/stores';
import { couponsAPI, Coupon } from '../api/coupons';
import { useAuthStore } from '../stores/authStore';
import { useLocationStore } from '../stores/locationStore';
import LocationPicker, { PickedLocation } from '../components/LocationPicker';
import { Button, Card, css, notify } from '../design';
import CouponInput from '../components/cart/CouponInput';
import TipSelector from '../components/checkout/TipSelector';
import TrustBadges from '../components/checkout/TrustBadges';
import OrderSummaryCard, { SummaryLine } from '../components/checkout/OrderSummaryCard';
import PaymentMethodSelector, { PaymentMethodId } from '../components/checkout/PaymentMethodSelector';
import FreeDeliveryProgress from '../components/cart/FreeDeliveryProgress';
import PageHead from '../components/seo/PageHead';

const FREE_DELIVERY_THRESHOLD = 299;
const DEFAULT_DELIVERY_FEE = 30;

export function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const savedLocation = useLocationStore((s) => s.current);
  const setSavedLocation = useLocationStore((s) => s.setCurrent);

  const [cart, setCart] = useState<Cart | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Map<number, Product>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // Form state
  const [deliveryAddress, setDeliveryAddress] = useState(savedLocation?.address || savedLocation?.label || '');
  const [deliveryLoc, setDeliveryLoc] = useState<PickedLocation | null>(
    savedLocation
      ? { address: savedLocation.address || savedLocation.label, latitude: savedLocation.latitude, longitude: savedLocation.longitude }
      : null,
  );
  const [addressDetails, setAddressDetails] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodId>('upi');
  const [tipAmount, setTipAmount] = useState<number>(0);

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { loadCheckoutData(); }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const loadCheckoutData = async () => {
    try {
      setLoading(true);
      if (!user) { navigate('/login'); return; }
      const urlParams = new URLSearchParams(window.location.search);
      const storeId = Number(urlParams.get('store_id')) || 1;
      const cartData = await cartAPI.getCart(storeId);
      setCart(cartData);
      const storeData = await storesAPI.getStore(storeId);
      setStore(storeData);
      // Hydrate products in parallel.
      const productMap = new Map<number, Product>();
      await Promise.all(
        cartData.items.map(async (item) => {
          try {
            const product = await productsAPI.getProduct(item.product_id);
            productMap.set(item.product_id, product);
          } catch { /* ignore — name falls back to "Product #id" */ }
        }),
      );
      setProducts(productMap);
      // Coupons (best effort).
      try {
        const coupons = await couponsAPI.listAvailableCoupons(storeId);
        setAvailableCoupons(coupons);
      } catch { /* no coupons available */ }
    } catch (err) {
      setError('Failed to load checkout data');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = (cart?.items ?? []).reduce((s, it) => s + it.price_at_add * it.quantity, 0);
  const deliveryFee = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DEFAULT_DELIVERY_FEE;
  const total = subtotal + deliveryFee + tipAmount - discountAmount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cart || !store) { setError('Cart or store data missing'); return; }
    if (!deliveryAddress.trim() || !deliveryLoc) {
      setError('Please pick a delivery address on the map');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Persist the chosen location to the global locationStore so the rest
      // of the app (header chip, future orders) sees it.
      setSavedLocation({
        label: deliveryAddress.split(',').slice(0, 2).join(', ') || deliveryAddress,
        address: addressDetails ? `${deliveryAddress} (${addressDetails})` : deliveryAddress,
        latitude: deliveryLoc.latitude,
        longitude: deliveryLoc.longitude,
      });

      const orderData: CreateOrderRequest = {
        store_id: store.id,
        delivery_address: addressDetails
          ? `${deliveryAddress} — ${addressDetails}`
          : deliveryAddress,
        delivery_latitude: deliveryLoc.latitude,
        delivery_longitude: deliveryLoc.longitude,
        special_instructions: specialInstructions || undefined,
        delivery_fee: deliveryFee,
        discount_amount: discountAmount,
        coupon_code: appliedCoupon ? appliedCoupon.code : undefined,
      };
      const order = await ordersAPI.createOrder(orderData);

      if (paymentMethod === 'upi') {
        navigate(`/payment/${order.id}`);
      } else {
        navigate(`/order-confirmed/${order.id}`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create order');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" className="text-success" />
        <p className="mt-3" style={{ color: css.color.textMuted }}>Loading checkout...</p>
      </Container>
    );
  }

  if (!cart || !store || cart.items.length === 0) {
    return (
      <Container style={{ maxWidth: 720, paddingTop: 16, paddingBottom: 80 }}>
        <h2 style={{ fontFamily: css.font.display, fontWeight: 800, fontSize: 24 }}>Checkout</h2>
        <Card padding="lg" style={{ textAlign: 'center', marginTop: 16 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
          <h5 style={{ color: css.color.textMuted, marginBottom: 16 }}>Cart is empty</h5>
          <Button variant="primary" onClick={() => navigate('/stores')}>Back to shopping</Button>
        </Card>
      </Container>
    );
  }

  const summaryLines: SummaryLine[] = cart.items.map((it) => {
    const p = products.get(it.product_id);
    return {
      productId: it.product_id,
      name: p?.name || `Product #${it.product_id}`,
      quantity: it.quantity,
      price: it.price_at_add,
      imageUrl: p?.image_url,
      thumbnailUrl: p?.thumbnail_url,
    };
  });

  return (
    <Container style={{ maxWidth: 1100, paddingTop: 16, paddingBottom: 100 }}>
      <PageHead title="Checkout — ProvisionMart" noIndex />
      <Button
        variant="link"
        leadingIcon={<ArrowLeft size={16} />}
        onClick={() => navigate(`/cart?store_id=${store.id}`)}
        style={{ marginBottom: 8 }}
      >
        Back to cart
      </Button>

      <h2 style={{ fontFamily: css.font.display, fontWeight: 800, fontSize: 26, letterSpacing: '-0.02em', marginBottom: 6 }}>
        Checkout
      </h2>
      <div style={{ fontSize: 13, color: css.color.textMuted, marginBottom: 16 }}>
        Almost there — review your order and pick how you'd like to pay.
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ marginBottom: 12 }}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Left — form */}
        <Col lg={8}>
          <form onSubmit={handleSubmit}>
            {/* Delivery address */}
            <Card padding="md" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <MapPin size={18} color={css.color.primary} />
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>Delivery address</h5>
              </div>
              <LocationPicker
                placeholder="Search your delivery address…"
                value={deliveryLoc}
                onChange={(loc) => { setDeliveryLoc(loc); setDeliveryAddress(loc.address); }}
                height={240}
                dotColor={css.color.primary as any}
              />
              <input
                type="text"
                placeholder="House / flat / floor / landmark"
                value={addressDetails}
                onChange={(e) => setAddressDetails(e.target.value)}
                style={{
                  marginTop: 10,
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${css.color.border}`,
                  borderRadius: 10,
                  background: css.color.surface,
                  color: css.color.text,
                  fontSize: 14,
                  outline: 'none',
                }}
              />
              <div style={{ marginTop: 6, fontSize: 12, color: css.color.textMuted }}>
                Tap the map or drag the pin to set the exact drop point.
              </div>
            </Card>

            {/* Special instructions */}
            <Card padding="md" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Truck size={18} color={css.color.primary} />
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>Delivery instructions</h5>
              </div>
              <textarea
                rows={2}
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="e.g. Leave at the door, ring bell once."
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${css.color.border}`,
                  borderRadius: 10,
                  background: css.color.surface,
                  color: css.color.text,
                  fontSize: 14,
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
            </Card>

            {/* Free-delivery progress (visible in summary, but also here so it's obvious) */}
            <FreeDeliveryProgress
              subtotal={subtotal}
              threshold={FREE_DELIVERY_THRESHOLD}
              onShopMore={() => navigate(`/store/${store.id}`)}
            />

            {/* Coupon */}
            <CouponInput
              storeId={store.id}
              subtotal={subtotal}
              appliedCoupon={appliedCoupon}
              discountAmount={discountAmount}
              availableCoupons={availableCoupons}
              onApply={(res) => {
                setAppliedCoupon(res.coupon ?? null);
                setDiscountAmount(res.discount_amount);
                if (res.coupon) notify.success(`Saved ₹${res.discount_amount.toFixed(0)} with ${res.coupon.code}!`);
              }}
              onRemove={() => { setAppliedCoupon(null); setDiscountAmount(0); }}
            />

            {/* Tip */}
            <TipSelector value={tipAmount} onChange={setTipAmount} />

            {/* Payment */}
            <Card padding="md" style={{ marginBottom: 12 }}>
              <h5 style={{ margin: '0 0 10px', fontWeight: 700, fontSize: 16 }}>Payment method</h5>
              <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />
            </Card>

            {/* Trust + Place order */}
            <TrustBadges />

            <Button
              variant="primary"
              size="lg"
              fullWidth
              type="submit"
              loading={processing}
              style={{ marginTop: 4 }}
            >
              {processing
                ? 'Processing…'
                : paymentMethod === 'upi'
                  ? `Pay ₹${total.toFixed(0)} via UPI`
                  : `Place order · ₹${total.toFixed(0)}`}
            </Button>

            <div style={{ marginTop: 8, fontSize: 12, color: css.color.textMuted, textAlign: 'center' }}>
              By placing this order you agree to our Terms & Refund Policy.
            </div>
          </form>
        </Col>

        {/* Right — Summary */}
        <Col lg={4} className="mt-4 mt-lg-0">
          <OrderSummaryCard
            storeName={store.business_name}
            storeAddress={store.address}
            items={summaryLines}
            subtotal={subtotal}
            deliveryFee={deliveryFee}
            discountAmount={discountAmount}
            tipAmount={tipAmount}
            total={total}
            paymentMethodLabel={paymentMethod.toUpperCase()}
            productsMap={products}
          />
        </Col>
      </Row>
    </Container>
  );
}

export default CheckoutPage;

